import { GtmUtils } from './gtm-utils';

export default class FunnelForm {
  constructor() {
    this.progressBar = document.querySelector('progress');
    this.hasProgressBar = !!this.progressBar;
    this.funnel = document.querySelector('aside.funnel');
    this.form = this.funnel.querySelector('form');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.advanceSlideButtons = document.querySelectorAll(
      'button[data-onclick="advance-slide-handle"]',
    );
    this.successSlides = [
      ...document.querySelectorAll('section.hidden'),
    ].filter((el) => el.dataset.conversionGroup);
    this.selectedConversionGroup = null;
    this.lastAnswer = null;
    this.lastQuestion = null;
    this.intervalSetGAClient = null;
    this.delaySetGAClientId = 1000;
    // We need mapping of Freeform fields to make sure that they are added to
    // datalayer with their respective key mapping
    // (Note: We can have multiple `fieldHandle` for the same `GTM key`).
    // In the below object, key represents the `fieldHandle` and value represents the `GTM key`.
    this.mappingOfFieldHandleToGTMKey = {
      zipCode: 'address.postal_code',
      companyName: 'companyName',

      city: 'address.city',
      tippgeberCity: 'address.city',

      email: 'email',
      eMailInteressierten: 'email',
      tippgeberPartnerID: 'email',

      tippgeberPhoneNum: 'phone_number',
      telefonInteressierten: 'phone_number',
      telefonWerbenden: 'phone_number',
      telefonInteressenten: 'phone_number',
      cellPhone: 'phone_number',
      homePhone: 'phone_number',
    };

    this.init();
  }

  // Set Google Tag Manager Event in dataLayer
  setGTMEvent(slide) {
    const funnelName = this.funnel.dataset.funnelname;
    const completed = slide.dataset.completed || 'false';
    const label = slide.dataset.page;
    const event = slide.dataset.conversion
      ? 'ob.funnel.showForm'
      : 'ob.funnel.slide';

    const data = {
      funnelName,
      completed,
      event,
      label,
    };

    // Store the user answer on the dataLayer, and reset the value
    if (this.lastAnswer) {
      this.setAnswers(data);
    }

    this.setFormDataInGTM(slide, data);

    GtmUtils.getDataLayer().push(data);
  }

  setAnswers(data) {
    if (this.lastQuestion) {
      // set the answers to the dataLayer with same property name as the question
      data[this.lastQuestion] = this.lastAnswer;
    }
    data.answer = this.lastAnswer;
    this.lastAnswer = null;
    this.lastQuestion = null;
  }

  // Set Freeform data in GTM datalayer
  setFormDataInGTM(slide, data) {
    if (!slide || !slide.classList.contains('step_fieldset')) {
      return;
    }

    slide.querySelectorAll('input').forEach((input) => {
      const { name, value } = input;

      if (name in this.mappingOfFieldHandleToGTMKey) {
        const keyPath = this.mappingOfFieldHandleToGTMKey[name];
        this.setNestedKeyInObject(data, keyPath, value);
      }
    });
  }

  /**
   * Util function to set nested key in object
   *
   * Example: this.setNestedKeyInObject({ value: 1 }, 'address.city', 'Berlin')
   * Output: { value: 1, address: { city: 'Berlin' } }
   */
  setNestedKeyInObject(obj, path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const lastObj = keys.reduce((obj, key) => (obj[key] = obj[key] || {}), obj);
    lastObj[lastKey] = value;
  }

  // Set Google Analytics ID
  setGAClientId() {
    const field = document.querySelector('input[name="gaClientId"]');
    if (!window.gaGlobal || !window.gaGlobal.vid || !field) return;
    const clientId = window.gaGlobal.vid;
    field.value = clientId;
    clearInterval(this.intervalSetGAClient);
  }

  validateForm() {
    const slide = this.form.querySelector('.slide:not(.hidden)');
    const fields = slide.querySelectorAll('input:not([type="hidden"])');
    let invalidFieldsCount = 0;

    // I'm sorry, I only had time to kinda refactor this... It was worse :(
    fields.forEach((field) => {
      if (field.matches(':invalid')) {
        invalidFieldsCount += 1;
        if (field.value.trim() === '') {
          field.nextElementSibling.innerHTML = field.title;
        }
        if (field.value.trim() !== '' && !field.checkValidity()) {
          field.nextElementSibling.innerHTML = field.dataset.mismatch;
        }
        field.nextElementSibling.classList.remove('hidden');
        return;
      }
      field.nextElementSibling?.classList.add('hidden');
    });

    return invalidFieldsCount === 0;
  }

  updateProgressBar(val = null) {
    if (val) {
      this.progressBar.setAttribute('value', val);
      return;
    }

    const currentValue = Number(this.progressBar.getAttribute('value'));
    const maxValue = Number(this.progressBar.getAttribute('max'));
    const steps = Number(this.progressBar.dataset.steps);
    let progress = currentValue + maxValue / steps;
    if (progress > 100) progress = 100;
    this.progressBar.setAttribute('value', progress);
  }

  // Get the final slide based on the user answer (or the default slide)
  getSuccessSlide() {
    const successSlide = this.successSlides.find(
      ({ dataset }) => dataset.conversionGroup === this.selectedConversionGroup,
    );

    // Fallback to 'none' which is always at index 0
    return successSlide || this.successSlides[0];
  }

  showNextSlide(slide, finish = false) {
    if (!slide) {
      console.error('Next slide not found');
      return;
    }

    // Show either the final slide or the next slide
    const nextSlide = finish
      ? this.getSuccessSlide()
      : slide.nextElementSibling;

    // Hide the current slide
    slide.classList.add('hidden');

    // Show the next slide
    nextSlide.classList.remove('hidden');

    // Handle Progressbar
    this.hasProgressBar && this.updateProgressBar(finish ? 100 : null);

    // Handle GTM events
    this.setGTMEvent(slide);
  }

  addListeners() {
    // Prevent submitting the form with the Enter key while an input is focused
    this.form.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' && ev.target.nodeName === 'INPUT') {
        ev.preventDefault();
        this.validateForm();
        return;
      }
    });

    // Submit button
    this.submitButton.addEventListener('click', (ev) => {
      // Validate form to display possible errors
      if (!this.validateForm()) {
        ev.preventDefault();
        return;
      }

      // Collect GTM values from the last field set
      const lastFieldSet = ev.target.closest('.step_fieldset');
      if (lastFieldSet) {
        this.setGTMEvent(lastFieldSet);
      }

      // Slide that wraps this button
      const parentSection = ev.target.closest('section');

      // Handle slide toggle
      this.showNextSlide(parentSection, true);

      // Set a GTM event with `completed: "true"`
      const finalSlide = this.getSuccessSlide();
      this.setGTMEvent(finalSlide);
    });

    // Next slide buttons
    this.advanceSlideButtons.forEach((button) => {
      button.addEventListener('click', (ev) => {
        ev.preventDefault();
        const { answer, conversionGroup, pseudosubmit } = button.dataset;

        // Store user answer in its respective hidden field
        if (answer) {
          const question = button.closest('[data-question]').dataset.question;
          const input = document.querySelector(`input[name="${question}"]`);
          input && input.setAttribute('value', answer);

          // Set the conversion group to match the final slide only if:
          // it hasn't been set, or if the value of the button isn't 'none'
          if (!this.selectedConversionGroup || conversionGroup !== 'none') {
            // Set it as 'none' if the button doesn't have a conversionGroup
            this.selectedConversionGroup = conversionGroup || 'none';
          }

          // Set the answer to be pushed to the dataLayer
          this.lastAnswer = answer;
          this.lastQuestion = question;
        }

        // Validate form to display possible errors
        if (pseudosubmit && !this.validateForm()) return;

        // Slide that contains this button
        const parentSection = button.closest('.slide');

        // Handle slide toggle
        this.showNextSlide(parentSection);
      });
    });
  }

  init() {
    if (!this.form.length) {
      console.error('Form not found');
      return;
    }

    this.addListeners();

    // GA is available only when cookies are accepted; keep waiting for consent
    this.intervalSetGAClient = setInterval(() => {
      this.setGAClientId();
    }, this.delaySetGAClientId);
  }
}
